import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowser } from '@angular/platform-browser';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

declare global {
  interface Window {
    eid: any;
    neogovTopNavbar: any;
    __APP_CONFIG__: any;
  }
}

async function bootstrap() {
  // TODO [CONTAINER-MIGRATION]: Currently hardcoded to use the compiled environment config
  // (via Angular fileReplacements in angular.json). This keeps the existing build pipeline intact
  // until DevOps is ready with the container ConfigMap setup.
  //
  // WHEN DEVOPS GIVES GO-AHEAD — replace the line below with:
  //   const config = await fetch('./assets/environment.json').then((r) => r.json());
  //
  // AND ALSO do the following cleanup (see container-migration.md for full details):
  //   1. Remove all fileReplacements blocks from angular.json
  //   2. Delete all environment.*.ts files except environment.local.ts
  //   3. Remove runFromContainerEnabled from IConfig (config.service.ts) and environment.json
  const config = environment;
  window.__APP_CONFIG__ = config;

  if (!config.production) {
    console.log('[Bootstrap] Configuration loaded:', {
      production: config.production,
      apiUrl: config.apiUrl,
      froalaLicenseKeyConfigured: !!config.froalaLicenseKey && config.froalaLicenseKey !== '${FROALA_LICENSE_KEY}'
    });
  }

  if (config.production) {
    enableProdMode();
  }

  // Load AppDynamics scripts synchronously before bootstrap (same behaviour as before)
  const firstMetaTag = document.head.getElementsByTagName('meta')[0];

  if (config.appDynamics.isEnabled) {
    const appDynamicsConfigurationScript = document.createElement('script');
    appDynamicsConfigurationScript.type = 'text/javascript';
    appDynamicsConfigurationScript.src = config.appDynamics.configurationScriptPath;
    appDynamicsConfigurationScript.onload = () => {
      window['adrum-config'].appKey = config.appDynamics.appKey;
    };

    if (firstMetaTag) {
      document.head.insertBefore(appDynamicsConfigurationScript, firstMetaTag.nextSibling);
    }

    const appDynamicsScript = document.createElement('script');
    appDynamicsScript.type = 'text/javascript';
    appDynamicsScript.src = config.appDynamics.scriptPath;

    if (firstMetaTag && firstMetaTag.nextSibling) {
      document.head.insertBefore(appDynamicsScript, firstMetaTag.nextSibling.nextSibling);
    }
  }

  function bootstrapAngular() {
    platformBrowser()
      .bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()] })
      .catch((reason) => console.error(reason));
  }

  function loadEidSdk(): Promise<void> {
    return new Promise((resolve) => {
      if (window.eid) {
        resolve();
        return;
      }

      const eidScript = document.createElement('script');
      eidScript.src = config.eidSdkUrl;
      eidScript.type = 'module';
      eidScript.onload = () => {
        resolve();
      };
      eidScript.onerror = () => {
        console.error('Failed to load EID SDK');
        resolve(); // Continue even if SDK fails to load
      };
      document.head.appendChild(eidScript);
    });
  }

  function loadTopNav(): Promise<void> {
    return new Promise((resolve) => {
      const topNavScript = document.createElement('script');
      topNavScript.src = config.topNavBundleUrl;
      topNavScript.async = true;
      topNavScript.defer = true;
      topNavScript.type = 'module';

      window.neogovTopNavbar = {
        portfolioId: 2,
        productCode: 'PFTO'
      };

      topNavScript.onload = () => {
        resolve();
      };
      topNavScript.onerror = () => {
        console.error('Failed to load TopNav');
        resolve(); // Continue even if TopNav fails to load
      };

      document.head.appendChild(topNavScript);
    });
  }

  function loadAiComponents(): Promise<void> {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = config.aiComponentsBundleUrl;
      script.type = 'module';
      script.onload = () => resolve();
      script.onerror = () => {
        console.error('Failed to load AI Components bundle');
        resolve();
      };
      document.head.appendChild(script);
    });
  }

  // Load TopNav, EID SDK, and AI Components bundle in parallel
  await Promise.all([loadTopNav(), loadEidSdk(), loadAiComponents()]);

  // Wait until the custom element is defined before bootstrapping Angular
  if ('customElements' in window) {
    if (customElements.get('ce-neogov-top-navbar')) {
      bootstrapAngular();
    } else {
      customElements.whenDefined('ce-neogov-top-navbar').then(bootstrapAngular);
    }
  } else {
    bootstrapAngular();
  }
}

bootstrap().catch((err) => console.error('Failed to initialize application', err));
